import { Text } from "@chakra-ui/react";
import { RpcError } from "grpc-web";
import { FunctionComponent, ReactElement, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Components
import DataTable from "../../../shared/DataTable/DataTable";

// Hooks
import useClaimsToast from "../../../hooks/useToast";

// Utils
import { getClaimReports } from "../../../utils/api/reports";
import { ClaimReportType } from "../../../utils/api/protos/messages/claims.message_pb";
import { dummyPaginatedApiResponse } from "../../../utils/consts";
import { CustomColumnFilterType, DataTableColumn, PaginatedApiResponse } from "../../../utils/types";

interface ClaimReportsTableProps {
	claimId: number;
}

const mapReportTypeLabel = (type: ClaimReportType): "Positive" | "Negative" =>
	type === ClaimReportType.NEGATIVE ? "Negative" : "Positive";

const ClaimReportsTable: FunctionComponent<ClaimReportsTableProps> = (props: ClaimReportsTableProps): ReactElement => {
	const toast = useClaimsToast();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [reportsData, setReportsData] = useState<PaginatedApiResponse>(dummyPaginatedApiResponse);
	const [searchParams] = useSearchParams();

	const columns: Array<DataTableColumn> = [
		{
			header: "Report Name",
			id: "report_name",
			filterable: false,
			minWidth: "15ch",
			isHidden: false,
			isSortable: false
		},
		{
			header: "Reported On",
			id: "reported_on",
			filterable: false,
			isDate: true,
			minWidth: "10ch",
			isHidden: false,
			isSortable: false
		},
		{
			header: "Type",
			id: "type",
			filterable: false,
			minWidth: "10ch",
			isHidden: false,
			isSortable: false,
			customFilter: {
				type: CustomColumnFilterType.Select,
				options: [
					{ label: "Positive", value: "Positive" },
					{ label: "Negative", value: "Negative" }
				]
			},
			valueMap: (type: string) =>
				type === "Positive" ? (
					<Text color="green.500" fontWeight="500">
						{type}
					</Text>
				) : (
					<Text color="red.500" fontWeight="500">
						{type}
					</Text>
				)
		}
	];

	const handleFetchData = (_cacheKey: string): void => {
		if (!props.claimId || props.claimId <= 0) {
			setReportsData(dummyPaginatedApiResponse);
			return;
		}

		setIsLoading(true);
		getClaimReports(props.claimId)
			.then((response) => {
				const returnedReportsData: Array<{ [key: string]: string | number }> = response
					.getReportsList()
					.map((report) => ({
						id: report.getId(),
						claim_id: report.getClaimId(),
						report_name: report.getReportName(),
						reported_on: report.getReportedOn(),
						type: mapReportTypeLabel(report.getType()),
						created_at: report.hasCreatedAt() ? report.getCreatedAt() : ""
					}));

				setReportsData({
					data: returnedReportsData,
					meta: dummyPaginatedApiResponse.meta
				});
			})
			.catch((err: RpcError) => {
				toast("Error", err.message || "There was a problem fetching reports", "error");
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	return (
		<>
			<DataTable
				data={reportsData}
				columns={columns}
				refetchTrigger={handleFetchData}
				existingParams={searchParams}
				shouldUpdateUrl={true}
				shouldShowSelection={false}
				shouldShowTableContainer={false}
				shouldShowPagination={false}
				shouldShowFilterToggle={false}
			/>
			{reportsData.data.length === 0 && !isLoading && (
				<Text pt="3" fontStyle="italic">
					There are no reports to show
				</Text>
			)}
		</>
	);
};

export default ClaimReportsTable;
